import styles from './GateLearnMoreWebviewPlus.module.css';
import useComponentCampaignDefaultProps from '../hooks/useComponentCampaignDefaultProps.js';
import IconGlobe from './IconGlobe.js';
import IconFullEpisodesVideo from './IconFullEpisodesVideo.js';
import IconMarketDataLiveStream from './IconMarketDataLiveStream.js';
import IconAccessOnTheGo from './IconAccessOnTheGo.js';
import IconCheckoutArrow from './IconCheckoutArrow.js';
import LinkAuthFlow from './LinkAuthFlow.js';

const valueProps = [
  {
    heading: 'Global livestream',
    description:
      'One, single stream for all domestic and global market coverage, from US to UK and all Asian markets.',
    icon: <IconGlobe />,
  },
  {
    heading: 'On-demand Full episodes',
    description:
      'Catch up on all the latest market coverage with an expanded library of episodes on-demand.* Availability subject to programming schedule.',
    fineprint: 'Cannot be accessed outside the U.S. until late 2024.',
    icon: <IconFullEpisodesVideo />,
  },
  {
    heading: 'Market Data Livestream',
    description:
      'Watch the new Market Data Stream to see corresponding real-time data as anchors and guest share their opinions and analysis.',
    icon: <IconMarketDataLiveStream />,
  },
  {
    heading: 'Access Anytime, On the Go',
    description:
      'Never miss a moment with access to CNBC on your mobile device.',
    icon: <IconAccessOnTheGo />,
  },
];

const plans = [
  {
    name: 'CNBC+',
    disclaimer: 'Currently on cnbc.com and iOS & Android apps.',
    detail: 'Watch every minute of market coverage, live and on-demand.',
    price: (
      <>
        $14.<sup>99</sup> /month
      </>
    ),
    ctaText: 'Watch CNBC Live',
    planClassName: styles.planPlus,
    product: 'plus',
  },
  {
    name: 'Pro',
    detail:
      'Gain exclusive access to industry-leading expert research, analysis, and premium content',
    price: (
      <>
        $299.<sup>99</sup> /year or $34.<sup>99</sup> /month
      </>
    ),
    ctaText: 'Become a Pro',
    planClassName: styles.planPro,
    product: 'pro',
  },
];

function GateLearnMoreWebviewPlus() {
  const { minWidth, maxWidth, minHeight, maxHeight } =
    useComponentCampaignDefaultProps();
  const style = {
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
  };

  return (
    <>
      <div
        id="root-component"
        style={style}
        className={styles.GateLearnMoreWebviewPlusContainer}
        data-testid="GateLearnMoreWebviewPlus"
      >
        <div className={styles.gate}>
          <section className={styles.intro}>
            <h1 className={styles.headline}>Watch the CNBC Livestream</h1>
            <p>
              Watch every minute of market coverage and stay informed on all
              breaking news, opinions, and analysis from pre-market buzz to the
              closing bell, and beyond. Miss a moment? Catch up with full
              episodes on-demand.
            </p>
          </section>
          <section className={styles.features}>
            <ul className={styles.featuresList}>
              {valueProps.map(({ heading, description, fineprint, icon }) => (
                <li key={heading} className={styles.featuresListItem}>
                  <h3 className={styles.featureListHeading}>
                    <span className={styles.featuresListIcon}>{icon}</span>
                    {heading}
                  </h3>
                  <p>{description}</p>
                  {fineprint && <p className={styles.fineprint}>{fineprint}</p>}
                </li>
              ))}
            </ul>
          </section>
          <section className={styles.plans}>
            <h2 className={styles.plansHeading}>Compare plan details</h2>
            <ul className={styles.horizontalScroller}>
              {plans.map(
                ({
                  name,
                  disclaimer,
                  detail,
                  price,
                  ctaText,
                  planClassName,
                  product,
                }) => (
                  <li key={name} className={planClassName}>
                    <div className={styles.planName}>
                      {name}
                      {disclaimer && (
                        <div className={styles.disclaimer}>{disclaimer}</div>
                      )}
                    </div>
                    <div className={styles.planDetail}>
                      {detail}
                      <div className={styles.price}>{price}</div>
                      <div className={styles.cta}>
                        <LinkAuthFlow
                          product={product}
                          modalType={'signUp'}
                          className={styles.checkoutButton}
                        >
                          {ctaText}
                          <span className={styles.icon}>
                            <IconCheckoutArrow />
                          </span>
                        </LinkAuthFlow>
                      </div>
                    </div>
                  </li>
                ),
              )}
            </ul>
          </section>
          <section className={styles.comparison}>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>CNBC+</th>
                  <th>Pro</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>CNBC Live &amp; Video</th>
                  <th></th>
                  <th></th>
                </tr>
                <tr>
                  <td>
                    <span className={styles.liveDot}></span>Livestream
                  </td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                </tr>
                <tr>
                  <td>Market Data Stream*</td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                </tr>
                <tr>
                  <td>Full Episodes On-Demand*</td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                </tr>
                <tr>
                  <td>Pro Talks</td>
                  <td></td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                </tr>
                <tr>
                  <th>Tools</th>
                  <th></th>
                  <th></th>
                </tr>
                <tr>
                  <td>Watchlists</td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                </tr>
                <tr>
                  <td>Advanced Charting</td>
                  <td></td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                </tr>
                <tr>
                  <td>Stock Screener</td>
                  <td></td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                </tr>
                <tr>
                  <th>Insight &amp; Opinion</th>
                  <th></th>
                  <th></th>
                </tr>
                <tr>
                  <td>Expert Insights &amp; Stock Picks</td>
                  <td></td>
                  <td>
                    <span className={styles.checkmark}></span>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
          <section className={styles.disclaimers}>
            *Market Data Stream currently on cnbc.com and iOS &amp; Android apps
            and full episodes cannot be accessed outside the U.S. until late
            2024.
          </section>
        </div>
      </div>
    </>
  );
}

GateLearnMoreWebviewPlus.displayName = 'GateLearnMoreWebviewPlus';

export default GateLearnMoreWebviewPlus;
